import { useEffect, useRef, useState } from "react";
import "./vision.css";

const mndNavn = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const REKKE = {
  speaker: ["cover", "vision", "manifest", "why", "room", "event", "dinner", "broadcast", "place", "invitation"],
  partner: ["cover", "vision", "manifest", "why", "room", "ambition", "event", "experience", "networking", "dinner", "broadcast", "place", "frontier", "pstandard", "founding", "closing"],
  internal: ["cover", "vision", "manifest", "why", "room", "ambition", "event", "experience", "networking", "dinner", "broadcast", "place", "frontier", "pstandard", "truth", "road"],
};

const TITLER = {
  cover: "The Next Shift", vision: "The vision", manifest: "The standard", why: "Why now", room: "The room",
  ambition: "Speaker ambition", event: "The event", experience: "AI experience",
  networking: "Networking", dinner: "The dinner", broadcast: "Broadcast", place: "The place",
  invitation: "The invitation", frontier: "Partner ambition", truth: "What must be true", road: "Road to April 13",
};
TITLER.founding = "Ways to partner";
TITLER.closing = "The invitation";
TITLER.pstandard = "Partner standard";

const FRONTIER = [
  ["Anthropic", "/images/logos/labs/anthropic-v2.svg", "Claude", "/images/logos/labs/claude.svg"],
  ["OpenAI", "/images/logos/labs/openai-v2.svg", "ChatGPT", "/images/logos/labs/chatgpt.svg"],
  ["Google DeepMind", "/images/logos/labs/deepmind-v2.svg", "Gemini", "/images/logos/labs/gemini.svg"],
];

const ROLLER = [
  ["Founding Partner", "The largest and most strategic position. Category exclusive."],
  ["Strategic Partner", "Strong visibility, guest access and a clear role in the summit experience."],
  ["Broadcast Partner", "Ownership around the live broadcast, content and digital reach."],
  ["Summit Dinner Partner", "Premium hospitality and relationship-building."],
  ["AI Experience Partner", "The physical technology experience and activations."],
];

const AMBISJON = {
  headline: [
    ["Kevin O'Leary", "Investor · Shark Tank", "/images/targets/kevin-oleary.jpg"],
    ["Garry Kasparov", "Chess legend · AI thinker", "/images/targets/garry-kasparov.jpg"],
    ["Scott Galloway", "Professor · Author", "/images/targets/scott-galloway.jpg"],
    ["Nicolai Tangen", "CEO · NBIM", "/images/targets/nicolai-tangen.jpg"],
    ["Peter Diamandis", "Entrepreneur · Futurist", "/images/targets/peter-diamandis.jpg"],
    ["Inga Strümke", "AI researcher · Author", "/images/targets/inga-strumke.jpg"],
  ],
  frontier: [
    ["Andrej Karpathy", "Eureka Labs · OpenAI & Tesla alumnus"],
    ["Andrew Ng", "DeepLearning.AI · AI Fund"],
    ["Jack Clark", "Co-founder · Anthropic"],
    ["Daniela Amodei", "President & co-founder · Anthropic"],
  ],
  builders: [
    ["John Markus Lervik", "Founder · Cognite"],
    ["Anton Osika", "Co-founder · Lovable"],
    ["Silvija Seres", "Technology investor · LØRN"],
    ["Ethan Mollick", "Professor · Wharton"],
  ],
};

export const datoLabel = (iso) => {
  if (!iso || iso.length < 10) return "April 2027";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${mndNavn[m - 1]} ${y}`;
};

const WaveCanvas = () => {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    let t = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const resize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);
    const N = 130;
    const FARGER = ["168,110,255", "96,126,255", "236,96,208"];
    const draw = () => {
      raf = requestAnimationFrame(draw);
      if (document.hidden) return;
      t += 0.0042;
      ctx.clearRect(0, 0, w, h);
      for (let l = 0; l < 3; l++) {
        for (let i = 0; i < N; i++) {
          const p = i / N;
          const x = p * w * 1.1 - w * 0.05;
          const y =
            h * (0.6 + l * 0.055) +
            Math.sin(p * 6.3 + t * (1.5 + l * 0.5) + l * 2.1) * h * 0.065 +
            Math.sin(p * 12.7 - t * (2.1 + l * 0.4)) * h * 0.024;
          const a = 0.3 * (0.5 + 0.5 * Math.sin(p * Math.PI)) * (1 - l * 0.22);
          ctx.fillStyle = `rgba(${FARGER[l]},${a})`;
          ctx.beginPath();
          ctx.arc(x, y, 1.15, 0, 6.283);
          ctx.fill();
        }
      }
    };
    raf = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);
  return <canvas ref={ref} className="vis-wave" aria-hidden="true" />;
};

const LITEN_SKJERM = typeof window !== "undefined" && window.matchMedia("(max-width: 900px)").matches;

const Chap = ({ id, bilde, cover, wave, concept, moerk, klasse, children, refCb }) => (
  <section id={id} ref={refCb} className={`vis-chap ${klasse || ""} ${bilde ? "har-bilde" : ""} ${cover ? "vis-cover" : ""} ${moerk ? "moerk" : ""}`} data-testid={`vis-chap-${id}`}>
    {bilde && <img src={bilde} alt="" className="vis-bg" />}
    {bilde && <div className="vis-bg-shade" />}
    {wave && !LITEN_SKJERM && <WaveCanvas />}
    <div className="vis-grain" />
    <div className="vis-inner">{children}</div>
    {concept && <p className="vis-concept-tag" data-testid={`vis-concept-${id}`}>Concept visual</p>}
  </section>
);

const Kicker = ({ nr, tx }) => (
  <div className="vis-kicker vis-fade">
    <span className="nr">{nr}</span>
    <span className="tx">{tx}</span>
  </div>
);

const PRESENT_MS = 8000;

export default function VisionDeck({ view, data, anbefaling, kanKontakte, pdfModus }) {
  const [aktiv, setAktiv] = useState("01");
  const [present, setPresent] = useState(false);
  const [paused, setPaused] = useState(false);
  const aktivRef = useRef("01");
  const presentRef = useRef(false);
  const scrollRef = useRef(null);
  const rootRef = useRef(null);
  const chapsRef = useRef({});

  presentRef.current = present;

  const ev = data?.event || {};
  const dato = datoLabel(ev.eventDate);
  const kap = ev.capacity ? `~${ev.capacity}` : "~700";
  const bgSrc = (src) => (pdfModus ? `/images/pdf/${src.split("/").pop().replace(/\.(webp|jpg)$/, ".jpg")}` : src);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            aktivRef.current = e.target.id;
            setAktiv(e.target.id);
          }
        });
      },
      { root: scrollRef.current, threshold: 0.45 }
    );
    Object.values(chapsRef.current).forEach((el) => el && obs.observe(el));
    return () => obs.disconnect();
  }, [view, data]);

  const reg = (id) => (el) => { chapsRef.current[id] = el; };
  const gaa = (id) => chapsRef.current[id]?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (pdfModus) return undefined;
    const sc = scrollRef.current;
    if (!sc) return undefined;
    let sist = 0;
    const paScroll = () => {
      const y = sc.scrollTop;
      document.body.classList.toggle("vis-chrome-skjult", y > sist && y > 120);
      sist = y;
    };
    sc.addEventListener("scroll", paScroll, { passive: true });
    return () => {
      sc.removeEventListener("scroll", paScroll);
      document.body.classList.remove("vis-chrome-skjult");
    };
  }, [pdfModus]);

  useEffect(() => {
    if (pdfModus) return;
    ["/images/vision-cover-v3.webp", "/images/vision-vision-expo.webp", "/images/grieghallen.webp",
     "/images/vision-networking-v3.jpg", "/images/vision-expo-v3.webp", "/images/vision-dinner-v4.webp",
     "/images/vision-broadcast.jpg"].forEach((src) => { const i = new Image(); i.src = src; });
  }, [pdfModus]);

  const seq = REKKE[view] || REKKE.internal;
  const kapitler = seq.map((_, i) => String(i + 1).padStart(2, "0"));
  const labels = Object.fromEntries(seq.map((t, i) => [kapitler[i], TITLER[t]]));
  const nr = (key) => String(seq.indexOf(key) + 1).padStart(2, "0");
  const har = (key) => seq.includes(key);

  const stoppPresent = () => {
    setPresent(false);
    setPaused(false);
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  };
  const startPresent = () => {
    rootRef.current?.requestFullscreen?.().catch(() => {});
    setPresent(true);
    setPaused(false);
  };

  useEffect(() => {
    const fs = () => {
      if (!document.fullscreenElement && presentRef.current) {
        setPresent(false);
        setPaused(false);
      }
    };
    document.addEventListener("fullscreenchange", fs);
    return () => document.removeEventListener("fullscreenchange", fs);
  }, []);

  useEffect(() => {
    const start = () => startPresent();
    window.addEventListener("vis-present", start);
    return () => window.removeEventListener("vis-present", start);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!present || paused) return undefined;
    const iv = setInterval(() => {
      const idx = kapitler.indexOf(aktivRef.current);
      if (idx >= kapitler.length - 1) {
        stoppPresent();
        return;
      }
      chapsRef.current[kapitler[idx + 1]]?.scrollIntoView({ behavior: "smooth" });
    }, PRESENT_MS);
    return () => clearInterval(iv);
  }, [present, paused, view]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const key = (e) => {
      const t = e.target?.tagName;
      if (t === "INPUT" || t === "TEXTAREA" || t === "SELECT") return;
      if (e.key === "Escape" && presentRef.current) {
        stoppPresent();
        return;
      }
      if (e.key === " " && presentRef.current) {
        e.preventDefault();
        setPaused((p) => !p);
        return;
      }
      const frem = ["ArrowDown", "ArrowRight", "PageDown"].includes(e.key) || (e.key === " " && t !== "BUTTON");
      const bak = ["ArrowUp", "ArrowLeft", "PageUp"].includes(e.key);
      if (!frem && !bak && e.key !== "Home" && e.key !== "End") return;
      e.preventDefault();
      const idx = kapitler.indexOf(aktivRef.current);
      let neste = idx;
      if (frem) neste = Math.min(kapitler.length - 1, idx + 1);
      else if (bak) neste = Math.max(0, idx - 1);
      else if (e.key === "Home") neste = 0;
      else if (e.key === "End") neste = kapitler.length - 1;
      chapsRef.current[kapitler[neste]]?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [view, data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="vis-root" ref={rootRef} data-testid={`vision-deck-${view}`}>
      <div className="vis-progress" data-testid="vis-progress">
        <span style={{ width: `${((kapitler.indexOf(aktiv) + 1) / kapitler.length) * 100}%` }} />
      </div>
      <div className="vis-logo" data-testid="vis-logo">
        <span className="wordmark">
          <span className="wordmark__bold">Norwegian</span>
          <span className="wordmark__light">AI Summit</span>
        </span>
      </div>
      <nav className="vis-rail" data-testid="vis-rail">
        {kapitler.map((k) => (
          <button key={k} type="button" onClick={() => gaa(k)} className={aktiv === k ? "on" : ""} data-label={labels[k]} data-testid={`vis-rail-${k}`}>
            {k}
          </button>
        ))}
      </nav>

      {present && (
        <div className="vis-present" data-testid="vis-present-kontroll">
          <button type="button" className="vis-present-btn on" onClick={() => setPaused((p) => !p)} data-testid="vis-present-pause-btn">
            {paused ? "Resume" : "Pause"}
          </button>
          <button type="button" className="vis-present-btn" onClick={stoppPresent} data-testid="vis-present-exit-btn">
            Exit
          </button>
        </div>
      )}

      <div className="vis-teller" data-testid="vis-teller">
        <span className="nr">{aktiv}</span> / {kapitler[kapitler.length - 1]} — {labels[aktiv]}
      </div>

      {kanKontakte && !pdfModus && (
        <button
          type="button"
          className={`vis-mob-cta ${aktiv === "01" || aktiv === kapitler[kapitler.length - 1] ? "skjult" : ""}`}
          onClick={() => window.dispatchEvent(new Event("vis-kontakt"))}
          data-testid="vis-mob-kontakt"
        >
          Contact
        </button>
      )}

      {!pdfModus && (
        <div className={`vis-swipe ${aktiv !== "01" ? "borte" : ""}`} aria-hidden="true" data-testid="vis-swipe-hint">
          <span className="tx">Swipe</span>
          <span className="chev" />
        </div>
      )}

      <div className="vis-scroll" ref={scrollRef}>
        {/* COVER — THE NEXT SHIFT */}
        <Chap id="01" bilde={bgSrc("/images/vision-cover-v3.webp")} cover wave={!pdfModus} refCb={reg("01")}>
          <p className="vis-brand vis-fade" style={{ fontSize: 11, letterSpacing: "0.38em" }}>
            Norwegian AI Summit <span className="gul">2027</span>
          </p>
          <h1 className="vis-h vis-cover-h vis-fade" style={{ marginTop: 22 }}>
            <span className="l1">The <span className="gul">Next</span></span>
            <span className="l2">Shift</span>
          </h1>
          <div className="vis-meta vis-fade" style={{ marginTop: "clamp(12px, 2vh, 20px)" }}>
            <span>{dato}</span>
            <span className="dot">·</span>
            <span>Grieghallen</span>
            <span className="dot">·</span>
            <span>Bergen, Norway</span>
          </div>
        </Chap>

        {/* THE VISION */}
        <Chap id={nr("vision")} bilde={bgSrc("/images/vision-vision-expo.webp")} concept refCb={reg(nr("vision"))}>
          <Kicker nr={nr("vision")} tx="The vision" />
          <h2 className="vis-h vis-fade" data-testid="vis-vision-tittel">
            <span className="gul">The</span> annual<br />Norwegian<br />AI Summit
          </h2>
          <p className="vis-vision-sub vis-fade" data-testid="vis-vision-sub">
            Where leaders, builders and investors <br />come to understand what comes next.
          </p>
          <div className="vis-meta vis-fade" data-testid="vis-vision-meta">
            <span>700 in the room</span>
            <span className="dot">·</span>
            <span>Broadcast worldwide</span>
            <span className="dot">·</span>
            <span>{dato}</span>
          </div>
        </Chap>

        {/* MANIFEST — én setning, hele rommet */}
        <Chap id={nr("manifest")} klasse="kap-man" refCb={reg(nr("manifest"))}>
          <div className="vis-man vis-fade" data-testid="vis-manifest">
            <p className="a">Not another conference.</p>
            <p className="b">The one you can't<br />afford to <span className="gul">miss</span>.</p>
          </div>
        </Chap>

        {/* WHY NOW — massivt statement, stille bunn */}
        <Chap id={nr("why")} klasse="kap-why" refCb={reg(nr("why"))}>
          <Kicker nr={nr("why")} tx="Why now" />
          <h2 className="vis-h vis-why3-h vis-fade">
            AI is moving fast.<br />No one owns the <span className="gul">room</span>.
          </h2>
          <div className="vis-why3 vis-fade" data-testid="vis-whynow">
            <div className="pkt">
              <p className="nr">01</p>
              <p className="t">The pressure is real</p>
              <p className="d">AI is reshaping every industry. The cost of falling behind is rising.</p>
            </div>
            <div className="pkt">
              <p className="nr">02</p>
              <p className="t">A board-level priority</p>
              <p className="d">Companies are investing in capability, insight and access.</p>
            </div>
            <div className="pkt sterk">
              <p className="nr">03</p>
              <p className="t">The open position</p>
              <p className="d">Norway still has no defining annual AI summit. <span className="gul-d">Yet.</span></p>
            </div>
          </div>
        </Chap>

        {/* THE ROOM — 700 */}
        <Chap id={nr("room")} refCb={reg(nr("room"))}>
          <Kicker nr={nr("room")} tx="The room" />
          <p className="vis-tall vis-fade" data-testid="vis-tall-700">700</p>
          <h2 className="vis-h vis-h-md vis-fade" style={{ marginTop: "clamp(8px, 2vh, 20px)" }}>
            Not thousands.<br /><span className="gul">Deliberately</span> limited.
          </h2>
        </Chap>

        {/* SPEAKER AMBITION — hero-drevet editorial (partner/internal) */}
        {har("ambition") && (
          <Chap id={nr("ambition")} klasse="kap-amb" refCb={reg(nr("ambition"))}>
            <div className="vis-amb3-topp" data-testid="vis-ambition">
              <div>
                <Kicker nr={nr("ambition")} tx="Speaker ambition" />
                <h2 className="vis-h vis-fade" data-testid="vis-amb-tittel">
                  The people worth<br /><span className="gul">travelling</span> for.
                </h2>
                <p className="intro vis-fade">
                  Global business voices, frontier AI leaders and the people building what comes next.
                </p>
              </div>
            </div>
            <div className="vis-amb3-vegg vis-fade" data-testid="vis-amb-headline">
              {AMBISJON.headline.map(([n, d, img], i) => (
                <figure key={n} className="kort">
                  <img src={img} alt={n} loading={pdfModus ? "eager" : "lazy"} />
                  <figcaption>
                    <p className="tg">Target 0{i + 1}</p>
                    <p className="pn">{n}</p>
                    <p className="pd">{d}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
            <div className="vis-amb3-lister vis-fade">
              <div data-testid="vis-amb-frontier">
                <p className="g">Frontier AI</p>
                <div className="navn">
                  {AMBISJON.frontier.map(([n, d]) => (
                    <div key={n}><p className="nv">{n}</p><p className="md">{d}</p></div>
                  ))}
                </div>
              </div>
              <div data-testid="vis-amb-builders">
                <p className="g">Builders &amp; thinkers</p>
                <div className="navn">
                  {AMBISJON.builders.map(([n, d]) => (
                    <div key={n}><p className="nv">{n}</p><p className="md">{d}</p></div>
                  ))}
                </div>
              </div>
              <p className="note" data-testid="vis-amb-disclaimer">
                Confidential target universe.<br />
                Participation is not implied<br />
                unless explicitly confirmed.
              </p>
            </div>
          </Chap>
        )}

        {/* THE EVENT — dagens dramaturgi */}
        <Chap id={nr("event")} klasse="kap-event" refCb={reg(nr("event"))}>
          <Kicker nr={nr("event")} tx="The event" />
          <h2 className="vis-h vis-h-md vis-fade">
            One day.<br />Built around the <span className="gul">room</span>.
          </h2>
          <div className="vis-arc vis-fade" data-testid="vis-event-arc">
            <div className="soner">
              <div className="sone" data-testid="vis-arc-morning">
                <p className="tid">Morning</p>
                <p className="t">Main stage</p>
                <p className="u">Everyone together.</p>
                <p className="s">Keynotes · Firesides · Big ideas</p>
              </div>
              <div className="sone" data-testid="vis-arc-day">
                <p className="tid">Day</p>
                <p className="t">Theory <span className="pil">→</span> Practice</p>
                <p className="u">Same hall. The theme shifts through the day — from where AI is heading to what to do with it.</p>
                <p className="s">Networking · AI Experience · Partner activations</p>
              </div>
              <div className="sone" data-testid="vis-arc-evening">
                <p className="tid">Evening</p>
                <p className="t">Happy hour <span className="pil">→</span> <span className="gul-del">Summit dinner</span></p>
                <p className="u">The room unwinds. The conversation continues.</p>
                <p className="s">Speakers · Partners · Selected guests</p>
              </div>
            </div>
          </div>
          <p className="vis-arc-note vis-fade" data-testid="vis-dag-note">
            The breaks are part of the <span className="gul">product</span>.
          </p>
        </Chap>

        {/* AI EXPERIENCE */}
        {har("experience") && (
          <Chap id={nr("experience")} bilde={bgSrc("/images/vision-expo-v3.webp")} concept moerk refCb={reg(nr("experience"))}>
            <Kicker nr={nr("experience")} tx="The AI experience" />
            <h2 className="vis-h vis-h-md vis-fade">
              See it. Test it.<br />Talk to the people <span className="gul">building</span> it.
            </h2>
            <div className="vis-words vis-fade">
              {["Robotics", "Agents", "Autonomy", "Industrial AI", "AI-native software"].map((w) => (
                <span key={w}><i className="pk" />{w}</span>
              ))}
            </div>
          </Chap>
        )}

        {/* NETWORKING */}
        {har("networking") && (
          <Chap id={nr("networking")} bilde={bgSrc("/images/vision-networking-v3.jpg")} concept refCb={reg(nr("networking"))}>
            <Kicker nr={nr("networking")} tx="Networking" />
            <h2 className="vis-h vis-h-md vis-fade">The people you meet matter as much as the people on stage.</h2>
            <div className="vis-words vis-fade">
              {["Founders", "CEOs", "Investors", "Researchers", "Engineers", "Decision-makers"].map((w) => (
                <span key={w}><i className="pk" />{w}</span>
              ))}
            </div>
          </Chap>
        )}

        {/* SUMMIT DINNER */}
        <Chap id={nr("dinner")} bilde={bgSrc("/images/vision-dinner-v4.webp")} concept refCb={reg(nr("dinner"))}>
          <Kicker nr={nr("dinner")} tx="The summit dinner" />
          <h2 className="vis-h vis-h-md vis-fade">The conversation continues after the stage closes.</h2>
          <p className="vis-calibre-note vis-fade" style={{ marginTop: "clamp(24px, 4vh, 40px)" }} data-testid="vis-dinner-linje">
            Speakers · Partners · Selected guests
          </p>
        </Chap>

        {/* BROADCAST */}
        <Chap id={nr("broadcast")} bilde={bgSrc("/images/vision-broadcast.jpg")} concept moerk klasse="kap-cast" refCb={reg(nr("broadcast"))}>
          <Kicker nr={nr("broadcast")} tx="Built for broadcast" />
          <h2 className="vis-h vis-h-md vis-fade">Built for the room.<br />Produced for the world.</h2>
          <div className="vis-facts vis-fade">
            <div><p className="k">Production</p><p className="v">Broadcast-grade, multi-camera</p></div>
            <div><p className="k">Live</p><p className="v">Global digital audience</p></div>
            <div><p className="k">On-demand</p><p className="v">Every session, after the day</p></div>
            <div><p className="k">Content</p><p className="v">Year-round distribution</p></div>
          </div>
        </Chap>

        {/* THE PLACE — hvorfor Bergen, Grieghallen og Peer Gynt */}
        <Chap id={nr("place")} klasse="kap-place" refCb={reg(nr("place"))}>
          <div className="vis-place" data-testid="vis-place">
            <div className="venstre">
              <Kicker nr={nr("place")} tx="The place" />
              <h2 className="vis-h vis-fade" data-testid="vis-place-tittel">
                This is happening <span className="gul">here</span>.
              </h2>
              <div className="grunner vis-fade" data-testid="vis-place-grunner">
                <div>
                  <p className="g">Bergen</p>
                  <p className="d">Global industries. Strong technology communities. A city positioned to become a leading Nordic AI hub.</p>
                </div>
                <div>
                  <p className="g">Grieghallen</p>
                  <p className="d">Iconic, central and built for a premium live experience.</p>
                </div>
                <div>
                  <p className="g">Peer Gynt Hall</p>
                  <p className="d">~700 people. Deliberately limited. Designed for intimacy and impact.</p>
                </div>
              </div>
              <p className="slutt vis-fade" data-testid="vis-place-slutt">
                Norwegian AI Summit can help make Bergen a place the AI industry <span className="gul">travels to</span>.
              </p>
            </div>
            <figure className="hoyre vis-fade">
              <img src={bgSrc("/images/grieghallen.webp")} alt="Grieghallen, Bergen" />
              <figcaption data-testid="vis-place-proof">Peer Gynt Hall · Reserved · 13 April 2027</figcaption>
            </figure>
          </div>
        </Chap>

        {/* THE INVITATION (speaker) */}
        {har("invitation") && (
          <Chap id={nr("invitation")} refCb={reg(nr("invitation"))}>
            <Kicker nr={nr("invitation")} tx="The invitation" />
            <h2 className="vis-h vis-fade">We want <span className="gul">you</span> in the room.</h2>
            <div className="vis-facts vis-fade">
              <div><p className="k">Date</p><p className="v">{dato}</p></div>
              <div><p className="k">Venue</p><p className="v">Peer Gynt Hall, Grieghallen, Bergen</p></div>
              <div><p className="k">Format</p><p className="v">Keynote · fireside · conversation</p></div>
              <div><p className="k">Audience</p><p className="v">{kap} leaders, builders and investors</p></div>
              <div><p className="k">Broadcast</p><p className="v">Global digital distribution</p></div>
            </div>
            <div className="vis-kontakt vis-fade">
              <a href="mailto:martin@norwegianaisummit.no" data-testid="vis-kontakt-epost">martin@norwegianaisummit.no</a>
              <p>Martin · Norwegian AI Summit · Shared by invitation — please don't distribute.</p>
            </div>
          </Chap>
        )}

        {/* PARTNER AMBITION — frontier-relasjon + den norske sirkelen */}
        {har("frontier") && (
          <Chap id={nr("frontier")} klasse="kap-front" refCb={reg(nr("frontier"))}>
            <Kicker nr={nr("frontier")} tx="Partner ambition" />
            <h2 className="vis-h vis-h-md vis-fade">
              One global AI lab.<br />One partner per <span className="gul">category</span>.
            </h2>
            <div className="vis-std vis-fade" data-testid="vis-frontier-linjer">
              <p>One frontier AI lab anchors the technology story.</p>
              <p>One Norwegian partner owns each category.</p>
              <p className="sterk">Fewer partners. Stronger positions. No overlap.</p>
            </div>
            <div className="vis-front-labs vis-fade" data-testid="vis-frontier">
              <p className="g">Frontier targets</p>
              <div className="rekke">
                {FRONTIER.map(([n, logo, prod], i) => (
                  <div key={n} className="lab" data-testid={`vis-lab-${i + 1}`}>
                    <img src={logo} alt={n} />
                    <span>{prod}</span>
                  </div>
                ))}
              </div>
            </div>
            <p className="vis-std-kjede vis-fade" data-testid="vis-frontier-sirkel">
              {["Founding", "Strategic", "Broadcast", "Summit dinner", "AI experience"].map((w, i, a) => (
                <span key={w}>{w}{i < a.length - 1 && <i className="pk">·</i>}</span>
              ))}
            </p>
            <p className="vis-std-f1 vis-fade" data-testid="vis-frontier-note">
              Targets only — no conversations implied until explicitly stated.
            </p>
          </Chap>
        )}

        {/* PARTNER STANDARD — built to perform */}
        {har("pstandard") && (
          <Chap id={nr("pstandard")} klasse="kap-pstd" refCb={reg(nr("pstandard"))}>
            <Kicker nr={nr("pstandard")} tx="Partner standard" />
            <h2 className="vis-h vis-fade" data-testid="vis-pstd-tittel">
              Built to perform.<br />Built to reflect on <span className="gul">you</span>.
            </h2>
            <div className="vis-std vis-fade" data-testid="vis-pstd-linjer">
              <p>A partnership with Norwegian AI Summit is not a logo placement.</p>
              <p className="sterk">It is an association with how the entire experience is designed, produced and delivered.</p>
            </div>
            <div className="vis-pstd vis-fade" data-testid="vis-pstd-grid">
              {["World-class content", "Premium production", "Curated access", "High-end hospitality", "Broadcast-grade distribution", "Category exclusivity"].map((w, i) => (
                <div key={w}><p className="nr">0{i + 1}</p><p className="t">{w}</p></div>
              ))}
            </div>
            <p className="vis-std-f1 vis-fade" data-testid="vis-pstd-f1">Think Formula 1, not trade show.</p>
          </Chap>
        )}

        {/* WAYS TO PARTNER (partner) */}
        {har("founding") && (
          <Chap id={nr("founding")} klasse="kap-roller" refCb={reg(nr("founding"))}>
            <Kicker nr={nr("founding")} tx="Ways to partner" />
            <h2 className="vis-h vis-h-md vis-fade">
              One role.<br />Clear <span className="gul">ownership</span>.
            </h2>
            <p className="vis-roller-intro vis-fade">
              We are building a small circle of partners — each with a distinct position in the summit.
            </p>
            <div className="vis-roller vis-fade" data-testid="vis-roller">
              {ROLLER.map(([navn, eier], i) => {
                const anbefalt = anbefaling?.role === navn;
                return (
                  <div key={navn} className={`rolle ${anbefalt ? "anbefalt" : ""}`} data-testid={`vis-rolle-${i + 1}`}>
                    <span className="nr">0{i + 1}</span>
                    <p className="t">{navn}</p>
                    <p className="d">{eier}</p>
                    <span className="en" data-testid={anbefalt ? "vis-rolle-anbefalt" : undefined}>
                      {anbefalt ? `Recommended${anbefaling?.recipient ? ` for ${anbefaling.recipient}` : ""}` : "1 of 1"}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="vis-roller-cta vis-fade">
              {kanKontakte && !pdfModus ? (
                <button
                  type="button"
                  className="vis-kontakt-btn"
                  onClick={() => window.dispatchEvent(new Event("vis-kontakt"))}
                  data-testid="vis-kontakt-aapne"
                >
                  Start the conversation
                </button>
              ) : (
                <a className="vis-kontakt-btn" href="mailto:contact@norwegianaisummit.no" data-testid="vis-kontakt-epost">
                  contact@norwegianaisummit.no
                </a>
              )}
              <p className="notat">
                Commercial terms are shared in conversation<br />
                Shared by invitation — please don't distribute
              </p>
            </div>
          </Chap>
        )}

        {/* CLOSING (partner) — super-enkel, ala manifest */}
        {har("closing") && (
          <Chap id={nr("closing")} klasse="kap-man kap-slutt" refCb={reg(nr("closing"))}>
            <div className="vis-man vis-fade" data-testid="vis-closing">
              <p className="a">Norwegian AI Summit · {dato}</p>
              <p className="b">A few will help<br /><span className="gul">shape</span> it.</p>
              <p className="sub" data-testid="vis-closing-sub">
                We are building Norwegian AI Summit with a small number of category-exclusive
                partners — each with a meaningful role in the room.
              </p>
            </div>
            <div className="vis-slutt-cta vis-fade">
              {kanKontakte && !pdfModus ? (
                <button
                  type="button"
                  className="vis-kontakt-btn"
                  onClick={() => window.dispatchEvent(new Event("vis-kontakt"))}
                  data-testid="vis-slutt-kontakt-btn"
                >
                  Request a private conversation
                </button>
              ) : (
                <a className="vis-kontakt-btn" href="mailto:contact@norwegianaisummit.no" data-testid="vis-slutt-kontakt-epost">
                  contact@norwegianaisummit.no
                </a>
              )}
              <p className="notat" data-testid="vis-slutt-meta">Grieghallen · Bergen, Norway</p>
            </div>
          </Chap>
        )}

        {/* WHAT MUST BE TRUE (internal) */}
        {har("truth") && (
          <Chap id={nr("truth")} refCb={reg(nr("truth"))}>
            <Kicker nr={nr("truth")} tx="Internal" />
            <h2 className="vis-h vis-fade">What must<br />be <span className="gul">true</span>.</h2>
            <div className="vis-check vis-fade" data-testid="vis-must-be-true">
              {[
                "Anchor speaker secured",
                "Founding partner secured",
                "A programme worth travelling for",
                "550+ paid physical guests",
                "World-class broadcast",
                "An exceptional networking experience",
              ].map((t) => (
                <div key={t}><span className="st" /><p className="t">{t}</p></div>
              ))}
            </div>
            <p className="vis-calibre-note vis-fade" style={{ marginTop: 18 }} data-testid="vis-truth-note">
              Concept stage — live status lives in Event OS, not on this slide.
            </p>
          </Chap>
        )}

        {/* ROAD TO APRIL 13 (internal) */}
        {har("road") && (
          <Chap id={nr("road")} refCb={reg(nr("road"))}>
            <Kicker nr={nr("road")} tx="Road to April 13" />
            <h2 className="vis-h vis-h-md vis-fade">This happens<br />in <span className="gul">stages</span>.</h2>
            <div className="vis-road vis-fade" data-testid="vis-roadmap">
              {[
                ["Now → Sep", "Company, brand and speaker outreach"],
                ["Oct → Nov", "Anchor speaker and founding partner secured"],
                ["Dec → Jan", "Public launch and ticket sales"],
                ["Feb → Mar", "Programme, production and expo build"],
                ["April 13", "Showtime"],
              ].map(([nar, hva]) => (
                <div key={nar} className={nar === "April 13" ? "siste" : ""}><p className="nar">{nar}</p><p className="hva">{hva}</p></div>
              ))}
            </div>
          </Chap>
        )}
      </div>
    </div>
  );
}
